import FlagFootballPlay from "../sports/flag-football/play/flag-football-play";
import { FootballService } from "./football-service";
import TransitionBuilder from "../sports/football/state/transition-builder";
import LocalDataStrategy from "./api-client/base-data-strategy";
import FlagFootballTransitionConfig from "../sports/flag-football/state/flag-football-play-transtions";
import ContextService from "./context-service";
import FootballTransition from "../sports/football/state/football-transition";
import FootballState from "../sports/football/state/football-state";
import FlagPatCompleteStats from "../sports/flag-football/rules/general/scoring/pat-complete-stats";
import { isSafety } from "../sports/football/utils/context-utils";
import { isSeriesOvertime } from "../sports/football/utils/overtime-utils";
import { KICKOFF_DRIVE_END_TYPES } from "../sports/football/enums/drive-end-type";

class FlagFootballService extends FootballService {
    constructor() {
        super();
        this.localDataStrategy = new LocalDataStrategy();
    }

    async addPlay(gameId) {
        const play = new FlagFootballPlay();
        return this.baseAddPlay(gameId, play);
    }

    async getMetadata() {
        const transitionMetadata = FlagFootballTransitionConfig.map(
            (config) => {
                return Object.assign(
                    config,
                    TransitionBuilder.getByName(config.name)
                );
            }
        );

        return {
            transitions: transitionMetadata
        };
    }

    async _createPendingPlays(gameId) {
        let gameContext = await ContextService.getGameContext(gameId);
        const { triggerExtraOvertime, triggerPAT, triggerKO, currentDriveId } =
            gameContext;
        const { triggerDrive } = gameContext.getDriveContext();
        if (triggerPAT) {
            await this._finalizeDrive(gameContext);
            await this._createPendingPATPlay(gameId, gameContext.getTime());
        } else if (triggerKO && currentDriveId) {
            // Same branch as the base service. A standalone OT PAT does not
            // set triggerPAT, so the drive is not finalized above; without
            // this the kickoff it arms is entered into the still-open drive
            // and ValidationService throws KickoffIntoActiveDrive. Skip the
            // drive-end types whose rules already opened the next drive.
            // Finalizing here is also what keeps the kickoff in the drive it
            // starts rather than the one that just ended (PSTATS-1159).
            const driveEndType =
                gameContext.getDriveContext()?.previousDriveEndType?.code;
            if (!KICKOFF_DRIVE_END_TYPES.includes(driveEndType)) {
                await this._finalizeDrive(gameContext);
            }
        } else if (triggerDrive) {
            await this._finalizeDrive(gameContext);
            if (gameContext.getOvertimePeriod() && triggerExtraOvertime) {
                await this._createStartPeriodPlay(
                    gameId,
                    gameContext.getCurrentPeriod() + 1
                );
            }
            await this._startDriveWithoutPlay(gameId);
        }
    }

    _getApplicableTransitions(
        gameContext,
        play,
        transitions,
        fragment,
        coinTossRequired
    ) {
        if (fragment.associatedState === FootballState.AddPlay) {
            transitions = this._hideAddPlayTransitions(
                gameContext,
                transitions
            );
        } else {
            transitions = this._hideApplicableTransitions(
                gameContext,
                play,
                transitions,
                fragment,
                coinTossRequired
            );
        }
        return transitions;
    }

    _hideApplicableTransitions(
        gameContext,
        play,
        transitions,
        fragment,
        coinTossRequired
    ) {
        // TODO: Maybe we can remove this.
        transitions = fragment
            ? play.getStateTransitions(fragment.associatedState)
            : play.getAllowedTransitions();
        //

        if (coinTossRequired) {
            return transitions.filter(
                (transition) => transition.name === FootballTransition.CoinToss
            );
        } else {
            // Drives are created automatically; never offer a manual Start of
            // Drive action to operators.
            transitions = transitions.filter(
                (transition) =>
                    transition.name !== FootballTransition.StartDrive
            );
            // Sacks are opt-out per season or event. Configs saved before the
            // key existed lack it, so only an explicit false hides the option.
            if (gameContext.gameConfig?.general?.sackFlowEnabled === false) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.PassSacked
                );
            }
            // Fumbles are opt-out on the same terms. Fumble is the only
            // way into the recovered / lost subtree, so this one filter
            // closes the whole flow wherever it is offered: scrimmage plays,
            // kick and punt returns, laterals, and after a fumbled snap.
            if (gameContext.gameConfig?.general?.fumbleFlowEnabled === false) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.Fumble
                );
            } else {
                // Dead Ball exists to keep the bad-snap flow completable once
                // Fumble is gated off. While fumbles are available the Fumble
                // path is the richer record - it can name a recoverer, or TM /
                // NA when nobody is known - so the two are never offered
                // together.
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.DeadBall
                );
            }
            // Returning an intercepted try is opt-in per season or event.
            if (
                play.historyContains(FootballState.PatTry) &&
                gameContext.gameConfig?.general?.defensivePatReturnEnabled !==
                    true
            ) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.PassIntercepted
                );
            }
            if (fragment && !isSafety(play, fragment, gameContext)) {
                transitions = transitions.filter(
                    (t) => FootballTransition.Safety !== t.name
                );
            }

            return transitions;
        }
    }

    _hideAddPlayTransitions(gameContext, transitions) {
        const { triggerPAT, triggerKO, currentDriveId, coinTossRequired } =
            gameContext;

        // Kick is only ever a kickoff in flag football - there are no field
        // goals - so it is offered here and nowhere else.
        const allowedKickoffTransitions = [
            FootballTransition.PlayPenalty,
            FootballTransition.Kick,
            FootballTransition.GameControl
        ];
        const allowedPatTransitions = [
            FootballTransition.PatTry,
            FootballTransition.PlayPenalty,
            FootballTransition.GameControl
        ];
        const generalTransitions = [
            FootballTransition.PlayPenalty,
            FootballTransition.GameControl,
            FootballTransition.Pass,
            FootballTransition.Rush,
            FootballTransition.Punt,
            FootballTransition.Spike,
            FootballTransition.KneelDown
        ];
        // Fumbled Snap is opt-in per season or event.
        if (gameContext.gameConfig?.general?.fumbledSnapEnabled) {
            generalTransitions.push(FootballTransition.FumbledSnap);
        }
        if (triggerKO) {
            transitions = transitions.filter((t) =>
                allowedKickoffTransitions.some(
                    (transition) => transition === t.name
                )
            );
        } else if (triggerPAT) {
            transitions = transitions.filter((t) =>
                allowedPatTransitions.some(
                    (transition) => transition === t.name
                )
            );
        } else if (!currentDriveId || coinTossRequired) {
            transitions = this._filterGameCtrlTransitions(transitions);
        } else {
            // Standalone tries (PSTATS-1122) belong to series overtime; with
            // OT Kickoffs on, overtime offers what regulation does.
            if (isSeriesOvertime(gameContext)) {
                generalTransitions.push(FootballTransition.PatTry);
            }
            transitions = transitions.filter((t) =>
                generalTransitions.some((transition) => transition === t.name)
            );
        }
        return transitions;
    }

    async updatePatStats(originalPlay, originalFragment, updatedFragment) {
        const gameContext = await ContextService.getGameContext(
            originalPlay.gameId
        );
        const { statsFragment, points } =
            FlagPatCompleteStats._resolvePatStatsFragmentAndPoints(
                originalPlay,
                updatedFragment,
                gameContext
            );

        return this._doUpdatePatStats({ statsFragment, points });
    }
}

export default new FlagFootballService();
